import React, { useRef } from "react";
import {
  GestureResponderEvent,
  PanResponder,
  PanResponderGestureState,
} from "react-native";
import { View } from "react-native-ui-lib";
import { EnhancedEventDetectionContext } from "./context";

export interface PanResponderWrapperProps {
  onActivate?: () => void;
  onMove?: (evt: PanResponderGestureState) => void;
  onDeactivate?: () => void;
  onPress?: () => void;
  longPressActivationThreshold?: number;
  activationMethod?: ActivationMethod;
  children?: React.ReactNode;
}

const LONG_PRESS_THRESHOLD = 300;

export enum ActivationMethod {
  ON_PRESS = "ON_PRESS",
  ON_LONG_PRESS = "ON_LONG_PRESS",
}

enum ReleaseEvent {
  PRESS = "PRESS",
  END_PAN = "END_PAN",
}

export function EnhancedEventDetection({
  onActivate,
  onMove,
  onDeactivate,
  onPress,
  longPressActivationThreshold = LONG_PRESS_THRESHOLD,
  activationMethod = ActivationMethod.ON_LONG_PRESS,
  children,
}: PanResponderWrapperProps) {
  const activeRef = useRef<boolean>(false);
  const movedRef = useRef<boolean>(false);
  const prevGestureStateRef = useRef<PanResponderGestureState | null>();
  const inLongPress = useRef<boolean>(false);

  const longPressTimer = useRef<any>(null);

  const handleActivationRef = useRef(handleActivation);
  const handlePanRef = useRef(handlePan);
  const handleTerminationRequestRef = useRef(handleTerminationRequest);
  const handleReleaseRef = useRef(handleRelease);

  handleActivationRef.current = handleActivation;
  handlePanRef.current = handlePan;
  handleTerminationRequestRef.current = handleTerminationRequest;
  handleReleaseRef.current = handleRelease;

  function handleActivation() {
    if (activationMethod === ActivationMethod.ON_LONG_PRESS) {
      longPressTimer.current = setTimeout(
        handleLongPress,
        LONG_PRESS_THRESHOLD
      );
    }
  }

  function handleLongPress() {
    if (!!longPressActivationThreshold && movedRef.current) {
      return resetGestureState();
    }

    inLongPress.current = true;

    activate();
  }

  function activate() {
    activeRef.current = true;
    setTimeout(() => onActivate?.(), 0);
  }

  function handlePan(
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) {
    movedRef.current = true;
    if (!activeRef.current && activationMethod === ActivationMethod.ON_PRESS) {
      activate();
    }

    onMove?.(gestureState);
    prevGestureStateRef.current = gestureState;
  }

  function handleTerminationRequest(
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) {
    // If the gesture is a long press, don't terminate since
    // the gesture is handled by the component and dragged
    if (inLongPress.current === true) {
      return false;
    }

    // terminate the request if the gesture is a pan
    // so that the gesture can be handled by the parent
    const { dx, dy } = gestureState;
    return Math.abs(dx) > Math.abs(dy);
  }

  function resetGestureState() {
    clearTimeout(longPressTimer.current);
    inLongPress.current = false;
    prevGestureStateRef.current = null;
    movedRef.current = false;
    activeRef.current = false;
  }

  function handleRelease() {
    const releaseEvent = determineReleaseEvent(movedRef.current);

    resetGestureState();
    switch (releaseEvent) {
      case ReleaseEvent.PRESS:
        onPress?.();
        break;
      case ReleaseEvent.END_PAN:
        onDeactivate?.();
        break;
    }
  }

  function determineReleaseEvent(moved: boolean) {
    if (moved || inLongPress.current) {
      return ReleaseEvent.END_PAN;
    } else if (!moved) {
      return ReleaseEvent.PRESS;
    }
  }

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onStartShouldSetPanResponderCapture: () => true,
      onPanResponderGrant: () => {
        handleActivationRef.current?.();
      },
      onPanResponderMove: (e, s) => handlePanRef.current?.(e, s),
      onPanResponderTerminationRequest: (e, s) => {
        return handleTerminationRequestRef.current?.(e, s);
      },
      onPanResponderRelease: () => handleReleaseRef.current?.(),
    })
  ).current;

  return (
    <EnhancedEventDetectionContext.Provider
      value={{
        inLongPress: inLongPress.current,
        active: activeRef.current,
      }}
    >
      <View {...panResponder.panHandlers}>{children}</View>
    </EnhancedEventDetectionContext.Provider>
  );
}
